import { AgGridReact, CustomCellRendererProps } from '@mint-lib/ag-grid/react';
import { useCallback, useMemo } from 'react';

import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColDef,
  ColumnAutoSizeModule,
  ModuleRegistry,
  ValidationModule,
  type GetContextMenuItems,
} from '@mint-lib/ag-grid/community';
import { LoadingCell } from '@mint-lib/ag-grid/components';
import { ContextMenuModule } from '@mint-lib/ag-grid/enterprise';
import { useTranslation } from '@mint-lib/i18n';
import { Delete, Edit, OverflowMenuVertical } from '@mint-lib/icons';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@mint-lib/tw-components';
import { StaticDateSpan } from '@mint-lib/tw-components/patterns';
import { WhitelistedEmails } from '@mint-sdk/api-data-ingestion';

import { useIsReadOnlyUser } from '../../hooks/index.js';
import { GridMenuItem } from '../atoms/GridMenuItem.jsx';

ModuleRegistry.registerModules([
  CellStyleModule,
  ColumnAutoSizeModule,
  ClientSideRowModelModule,
  ContextMenuModule,
  ValidationModule,
]);

const SKELETON_ROWS = Array.from({ length: 10 }, () => ({ value: null }));

type WhitelistedEmailsTableProps = Readonly<{
  data: WhitelistedEmails.WhitelistedEmail[];
  isLoading: boolean;
  onEdit: (email: WhitelistedEmails.WhitelistedEmail) => void;
  onDelete: (email: WhitelistedEmails.WhitelistedEmail) => void;
}>;

export function WhitelistedEmailsTable({
  data,
  isLoading,
  onEdit,
  onDelete,
}: WhitelistedEmailsTableProps) {
  const { t } = useTranslation('@myn/import-hub');
  const isReadOnlyUser = useIsReadOnlyUser();

  const getContextMenuItems: GetContextMenuItems<WhitelistedEmails.WhitelistedEmail> =
    useCallback(
      (params) => {
        if (!params.node?.data || isReadOnlyUser) {
          return [];
        }

        const { data: row } = params.node;

        return [
          {
            name: 'edit-email',
            menuItem: GridMenuItem,
            menuItemParams: {
              onClick: () => onEdit(row),
              children: (
                <>
                  <Edit /> {t('Edit email')}
                </>
              ),
            },
          },
          {
            name: 'delete-email',
            menuItem: GridMenuItem,
            menuItemParams: {
              onClick: () => onDelete(row),
              className: 'fill-text-error text-text-error',
              children: (
                <>
                  <Delete /> {t('Delete')}
                </>
              ),
            },
          },
        ];
      },
      [isReadOnlyUser, onEdit, onDelete, t],
    );

  const defaultColDef = useMemo(
    (): ColDef => ({
      sortable: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      cellClass: '!flex !justify-start !items-center',
    }),
    [],
  );

  const columnDefs =
    useMemo((): ColDef<WhitelistedEmails.WhitelistedEmail>[] => {
      const columns: ColDef<WhitelistedEmails.WhitelistedEmail>[] = [
        {
          headerName: t('Email'),
          field: 'email',
          minWidth: 200,
          flex: 2.5,
        },
        {
          headerName: t('Creation date'),
          field: 'createdDate',
          cellRenderer: DateRenderer,
          minWidth: 120,
          flex: 1.5,
        },
        {
          headerName: t('Created by'),
          field: 'createdByEmail',
          minWidth: 150,
          flex: 2,
        },
        {
          headerName: t('Update date'),
          field: 'updatedDate',
          cellRenderer: NullableDateRenderer,
          minWidth: 120,
          flex: 1.5,
        },
        {
          headerName: t('Updated by'),
          field: 'updatedByEmail',
          valueFormatter: (params) => params.value ?? '—',
          minWidth: 150,
          flex: 2,
        },
        ...(isReadOnlyUser
          ? []
          : [
              {
                colId: 'actions',
                headerName: '',
                cellRenderer: ActionsRenderer,
                cellRendererParams: { onEdit, onDelete },
                pinned: 'right' as const,
                resizable: false,
                width: 40,
                minWidth: 40,
                cellClass: '!flex !justify-center !items-center',
              } satisfies ColDef<WhitelistedEmails.WhitelistedEmail>,
            ]),
      ];

      return columns;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReadOnlyUser, onEdit, onDelete]);

  const loadingColumnDefs = useMemo(
    (): ColDef[] =>
      columnDefs.map(
        ({ cellRenderer, cellRendererParams, valueFormatter, ...col }) => ({
          ...col,
          cellRenderer: LoadingCell,
        }),
      ),
    [columnDefs],
  );

  return (
    <div className="max-h-[calc(100vh-300px)] overflow-auto">
      <AgGridReact
        className="[&_.ag-center-cols-container]:min-h-0! [&_.ag-center-cols-viewport]:min-h-0!"
        rowData={isLoading ? SKELETON_ROWS : data}
        defaultColDef={defaultColDef}
        columnDefs={isLoading ? loadingColumnDefs : columnDefs}
        loading={false}
        suppressCellFocus
        suppressNoRowsOverlay
        domLayout="autoHeight"
        getRowId={isLoading ? undefined : (params) => params.data.id}
        getContextMenuItems={getContextMenuItems}
        popupParent={document.body}
      />
    </div>
  );
}

const DateRenderer = ({
  value,
}: CustomCellRendererProps<WhitelistedEmails.WhitelistedEmail>) => {
  if (!value) return null;
  return <StaticDateSpan date={value} />;
};

const NullableDateRenderer = ({
  value,
}: CustomCellRendererProps<WhitelistedEmails.WhitelistedEmail>) => {
  if (!value) return <span>—</span>;
  return <StaticDateSpan date={value} />;
};

const ActionsRenderer = ({
  data,
  onEdit,
  onDelete,
}: CustomCellRendererProps<WhitelistedEmails.WhitelistedEmail> & {
  onEdit: (email: WhitelistedEmails.WhitelistedEmail) => void;
  onDelete: (email: WhitelistedEmails.WhitelistedEmail) => void;
}) => {
  const { t } = useTranslation('@myn/import-hub');

  if (!data) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="plain"
          size="small"
          className="data-[state=open]:outline-2"
          aria-label={t('More actions')}
          data-dd-action-name="More actions"
        >
          <OverflowMenuVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        <DropdownMenuItem
          className="gap-2"
          data-dd-action-name="Edit whitelisted email"
          onClick={() => onEdit(data)}
        >
          <Edit />
          {t('Edit email')}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2 fill-text-error text-text-error"
          data-dd-action-name="Delete whitelisted email"
          onClick={() => onDelete(data)}
        >
          <Delete />
          {t('Delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
